/* eslint-disable */

import React, { useEffect, useState } from "react";
import "date-fns";
import DateFnsUtils from "@date-io/date-fns";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Snackbar,
  TextField,
  MenuItem,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Backdrop,
  Paper,
  Divider,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import nanoId from "nano-id";
import Markdown from "react-markdown";
import DeleteIcon from "@material-ui/icons/Delete";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import NoteIcon from "@material-ui/icons/Note";
import CategoryIcon from "@material-ui/icons/Category";
import DateRangeIcon from "@material-ui/icons/DateRange";
import ConfirmationNumberIcon from "@material-ui/icons/ConfirmationNumber";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import DescriptionIcon from "@material-ui/icons/Description";

import { useAuth } from "../../../../../contexts/AuthContext";
import { useCompany } from "../../../../../contexts/CompanyContext";
import { supabase } from "../../../../../supabase";
import { agentAssistCategories } from "../../../../shared/constants";
import SBFileUploader from "../../../../shared/SBFileUploader";
import ModalCard from "../../../../shared/ModalCard";
import sendAssistMessage from "../../../../shared/functions/sendAssistMessage.function";
import formatTicketNumber from "../../../../shared/functions/formatTicketNumber";
import { sendEmail } from "../../../../../api";

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    backgroundColor: theme.palette.primary.main,
    color: "#ffffff",
    paddingBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  markdownContainer: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
  },
  inputLabel: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
    "& svg": {
      marginRight: theme.spacing(1),
      color: theme.palette.text.secondary,
    },
  },
  termsSection: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  fileItem: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
  },
  fileIcon: {
    backgroundColor: theme.palette.grey[200],
  },
  submitButton: {
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    "& svg": {
      marginRight: theme.spacing(1),
      color: theme.palette.primary.main,
    },
  },
  noFilesMessage: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    marginBottom: theme.spacing(1),
  },
  uploadButton: {
    marginTop: theme.spacing(1),
  },
  dialogActionContainer: {
    padding: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
  },
}));

/**
 * AddAgentAssistTicketModal is a React component that renders a modal for adding agent support tickets.
 *
 * @param {object} props - The component props.
 * @param {string} props.supplier - The supplier name.
 * @param {Function} props.onClose - The function to be called when the modal is closed.
 * @param {boolean} props.open - A boolean indicating whether the modal is open or not.
 *
 * @returns {React.ReactElement} The element to be rendered.
 */
export default function AddAgentAssistTicketModal({ supplier, onClose, open }) {
  const classes = useStyles();

  const uploadBucket = "agent_assist";
  const now = new Date();
  const { currentUser } = useAuth();
  const { getSupplier } = useCompany();
  const defFormValues = {
    dealer_code: `Z-${currentUser.id}: ${currentUser.first_name} ${currentUser.surname}`,
    supplier,
    agreed_to_terms: false,
    category: "",
    status: "Open",
    last_updated_by_pretty: "New",
    supporting_doc_ref: "",
    user_note: "",
    supplier_ticket_date: now.toISOString().slice(0, 10),
    supplier_ticket_number: "",
    users_master_id: currentUser.id,
  };

  // State management
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [disable, setDisable] = useState(false);
  const [addSupportingDocsModal, setAddSupportingDocsModal] = useState(false);
  const [isFilesUploaded, setIsFilesUploaded] = useState(false);
  const [filesUploaded, setFilesUploaded] = useState([]);
  const [uploadId, setUploadId] = useState("");
  const [formValues, setFormValues] = useState(defFormValues);
  const [supplierDetail, setSupplierDetail] = useState(undefined);
  const [admins, setAdmins] = useState([]);

  // Fetch admins when supplier details are available
  useEffect(() => {
    const fetchAdmins = async () => {
      const { data, error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("suppliers_assist_email_index")
        .select("*, users_master(first_name, email)")
        .eq("related_supplier", supplierDetail?.id)
        .eq("role_type", 2)
        .eq("is_deleted", false);
      if (error) {
        console.error("Error fetching admins:", error);
      } else {
        setAdmins(data);
      }
    };

    if (supplierDetail?.id) {
      fetchAdmins();
    }
  }, [supplierDetail]);

  // Handle form changes
  const handleChange = event => {
    setDisable(false);
    const { name, value } = event.target;
    if (name === "agreed_to_terms") {
      setFormValues(prevFormValues => ({
        ...prevFormValues,
        [name]: !formValues.agreed_to_terms,
      }));
    } else {
      setFormValues(prevFormValues => ({
        ...prevFormValues,
        [name]: value,
      }));
    }
  };

  // Handle select changes
  const handleCategorySelect = event => {
    setDisable(false);
    setFormValues(prev => ({ ...prev, category: event.target.value }));
  };

  const handleSupplierDateSelect = selectedDate => {
    setFormValues(prev => ({
      ...prev,
      supplier_ticket_date: selectedDate.toISOString().slice(0, 10),
    }));
  };

  // File upload handling
  const handleIsFilesUploaded = () => setIsFilesUploaded(true);

  /**
   * Toggles the add supporting documents modal.
   * If the form is valid, the modal is toggled.
   * If the form is invalid, an error message is displayed.
   */
  const toggleAddSupportingDocsModal = () => {
    if (formValid()) {
      setAddSupportingDocsModal(old => !old);
    } else {
      setErrorMessage(
        "Please complete all required fields before uploading documents"
      );
      setIsSnackbarOpen(true);
    }
  };

  /**
   * Validates the form fields to ensure all required fields are completed.
   * @returns {boolean} True if the form is valid, false otherwise.
   */
  const formValid = () => {
    return (
      formValues.category &&
      formValues.supplier_ticket_date &&
      formValues.agreed_to_terms
    );
  };

  /**
   * Creates a new agent support ticket in the database.
   * @async
   * @function
   * @returns {Promise<void>}
   */
  const addAgentAssistLead = async () => {
    const { error, data } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("agent_assist")
      .insert(formValues)
      .select();

    const { id: ticket_id } = await data[0];
    if (error) {
      setErrorMessage("Error creating ticket entry");
      setIsSnackbarOpen(true);
      console.error(error.message);
    }

    sendAssistMessage(
      ticket_id,
      `
      ${formatTicketNumber(ticket_id, "ZAA")} created
      `,
      "update",
      "agent_assist_activity"
    );
  };

  /**
   * Sends an agent support email to the admin with details about the dealer and supplier involved.
   *
   * @param {{admin: object, ticket: object}} params - The parameters for the function.
   * @returns {Promise<string>} A promise that resolves with a success message or rejects with an error.
   */
  function sendAgentAssistEmail({ admin, ticket }) {
    return new Promise((resolve, reject) => {
      const email = [admin?.users_master.email];
      const subject = `New Agent Support for ${supplierDetail?.supplier_name}`;
      const message = `
      Dear ${admin?.users_master.first_name},
  
      The following dealer had submitted a new agent support ticket for this supplier: ${supplierDetail?.supplier_name}.
  
      Ticket Number: ${ticket?.supplier_ticket_number}
      Name: ${ticket?.dealer_code}
      Ticket Category: ${ticket?.category}
      Date Submitted: ${ticket?.supplier_ticket_date}
      User Note: ${ticket?.user_note}
  
  
      Best regards
      ZIMS Team
      `;
      // Send the email using the sendEmail function
      sendEmail({
        email, // Email to send to
        subject, // Email subject
        message, // Email message
      })
        .then(response => {
          resolve("Email sent successfully", response.data);
        })
        .catch(error => {
          console.error("Error sending email:", error);
          reject("Error sending email: ", error);
        });
    });
  }

  /**
   * Handles the add ticket event, validates form and submits data.
   * @async
   * @function
   * @returns {Promise<void>}
   */
  const handleAddTicket = async () => {
    setDisable(true);
    const ticket = formValues;
    if (formValid()) {
      try {
        await addAgentAssistLead();
        setFormValues(defFormValues);
        onClose();
      } catch (err) {
        setIsSnackbarOpen(true);
      }
    } else {
      setErrorMessage("Please complete all required fields");
      setIsSnackbarOpen(true);
      setDisable(true);
    }

    if (admins.length > 0 && formValid()) {
      await Promise.all(
        admins.map(async admin => {
          try {
            await sendAgentAssistEmail({
              admin,
              supplierDetail,
              ticket,
            });
          } catch (err) {
            console.error("Email error:", err);
          }
        })
      );
    }
    setDisable(false);
  };

  /**
   * Fetches uploaded files for the current ticket.
   * @async
   * @function
   * @returns {Promise<void>}
   */
  const fetchUploadedFiles = async () => {
    const { data: files, error } = await supabase.storage
      .from(uploadBucket)
      .list(uploadId);

    if (error) {
      throw error;
    }

    if (!files || !files.length) {
      setFilesUploaded([]);
      setIsFilesUploaded(false);
      setFormValues(old => ({ ...old, supporting_doc_ref: "" }));
    } else {
      setFilesUploaded(files);
      setFormValues(old => ({ ...old, supporting_doc_ref: uploadId }));
    }
  };

  /**
   * Fetches supplier details for the current ticket.
   * @async
   * @function
   * @returns {Promise<void>}
   */
  const fetchSupplierDetail = async () => {
    const supplierData = await getSupplier(supplier);
    setSupplierDetail(await supplierData);
  };

  // Effect to fetch uploaded files when the supporting docs modal is toggled
  useEffect(() => {
    if (isFilesUploaded) {
      fetchUploadedFiles();
    }
  }, [addSupportingDocsModal]);

  // Effect to set upload ID and fetch supplier details on component mount
  useEffect(() => {
    setUploadId(nanoId());
    fetchSupplierDetail();
  }, []);

  /**
   * Renders a list of uploaded files with delete options
   * @param {Array} files - List of uploaded files
   * @param {string} uploadId - Current upload identifier
   * @param {string} bucket - Storage bucket name
   * @param {Function} onUpdate - Callback after file actions
   * @param {Object} styleClasses - Style classes
   * @returns {React.ReactElement} The rendered file list
   */
  const FileListComponent = ({
    files,
    uploadId,
    bucket,
    onUpdate,
    styleClasses,
  }) => {
    if (!files || files.length === 0) {
      return (
        <Typography variant="body2" className={styleClasses.noFilesMessage}>
          No files uploaded
        </Typography>
      );
    }

    return (
      <List>
        {files.map(file => {
          const handleDelete = async () => {
            await supabase.storage
              .from(bucket)
              .remove([`${uploadId}/${file.name}`]);
            onUpdate();
          };

          let fileSize = "Unknown size";
          if (file.metadata && file.metadata.size) {
            fileSize = `${(file.metadata.size / 1024).toFixed(2)} KB`;
          }

          return (
            <ListItem key={file.id} className={styleClasses.fileItem}>
              <Avatar className={styleClasses.fileIcon}>
                <DescriptionIcon />
              </Avatar>
              <ListItemText
                primary={file.name}
                secondary={
                  <Typography variant="caption" color="textSecondary">
                    {fileSize}
                  </Typography>
                }
              />
              <ListItemSecondaryAction>
                <Tooltip title="Delete file">
                  <IconButton
                    edge="end"
                    color="secondary"
                    onClick={handleDelete}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">Agent Support Ticket</Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          {/* Supplier instructions section */}
          {supplierDetail?.agent_assist_text && (
            <Grid item xs={12}>
              <Paper variant="outlined" className={classes.markdownContainer}>
                <Box mb={1} display="flex" alignItems="center">
                  <InfoOutlinedIcon
                    fontSize="small"
                    color="primary"
                    style={{ marginRight: 8 }}
                  />
                  <Typography variant="subtitle1" color="primary">
                    {supplierDetail.supplier_name} Instructions
                  </Typography>
                </Box>
                <Divider style={{ marginBottom: 16 }} />
                <Markdown>{supplierDetail.agent_assist_text}</Markdown>
              </Paper>
            </Grid>
          )}

          {/* Category selection */}
          <Grid item xs={12}>
            <Box className={classes.sectionTitle}>
              <CategoryIcon />
              <Typography variant="subtitle1">Ticket Category</Typography>
            </Box>
            <TextField
              required
              select
              value={formValues.category}
              onChange={handleCategorySelect}
              id="category-select"
              label="Select category"
              variant="outlined"
              helperText="Select the appropriate category for your request"
              fullWidth
            >
              {agentAssistCategories[
                agentAssistCategories[formValues.supplier]
                  ? formValues.supplier
                  : "def"
              ].map(category => (
                <MenuItem key={category.key} value={category.value}>
                  {category.value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Date picker */}
          <Grid item xs={12} md={6}>
            <Box className={classes.sectionTitle}>
              <DateRangeIcon />
              <Typography variant="subtitle1">Ticket Date</Typography>
            </Box>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                disableToolbar
                fullWidth
                inputVariant="outlined"
                format="yyyy-MM-dd"
                margin="normal"
                id="date-picker-inline"
                style={{ marginTop: 0 }}
                label={`Date when ticket was logged with ${supplierDetail?.supplier_name}`}
                value={formValues.supplier_ticket_date}
                onChange={handleSupplierDateSelect}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </MuiPickersUtilsProvider>
          </Grid>

          {/* Supplier ticket number */}
          <Grid item xs={12} md={6}>
            <Box className={classes.sectionTitle}>
              <ConfirmationNumberIcon />
              <Typography variant="subtitle1">Supplier Ticket</Typography>
            </Box>
            <TextField
              label={`${supplierDetail?.supplier_name} ticket number`}
              variant="outlined"
              name="supplier_ticket_number"
              value={formValues.supplier_ticket_number}
              onChange={handleChange}
              fullWidth
              placeholder="Enter ticket number from supplier"
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <Box className={classes.sectionTitle}>
              <NoteIcon />
              <Typography variant="subtitle1">Additional Notes</Typography>
            </Box>
            <TextField
              label="Add any relevant details or special requirements"
              variant="outlined"
              name="user_note"
              value={formValues.user_note}
              onChange={handleChange}
              fullWidth
              multiline
              rows={4}
              placeholder="Enter any additional information that might help with your request"
            />
          </Grid>

          {/* Terms section */}
          <Grid item xs={12}>
            <Paper className={classes.termsSection} variant="outlined">
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Terms and Conditions
              </Typography>
              <Typography variant="body2" gutterBottom>
                *Please note that utilizing the agent support function does not
                guarantee the finalization, payment, or installation of an
                order. The purpose of utilizing the agent support function is
                solely for assistance.
              </Typography>
              <Typography variant="body2" gutterBottom>
                *When using the agent support function, please ensure to input
                the ticket details in the designated spaces.
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                *Service Level Agreement (SLA) Times: We are currently unable to
                provide precise SLA times. However, rest assured, we will
                provide updates and feedback as soon as possible.
              </Typography>
              <FormControlLabel
                style={{ marginTop: 16 }}
                control={
                  <Checkbox
                    required
                    color="primary"
                    checked={formValues.agreed_to_terms}
                    onChange={handleChange}
                    name="agreed_to_terms"
                  />
                }
                label={
                  <Typography variant="body2" color="textPrimary">
                    I agree to the terms and conditions
                  </Typography>
                }
              />
            </Paper>
          </Grid>

          {/* File upload section */}
          <Grid item xs={12}>
            <Box className={classes.sectionTitle}>
              <AttachFileIcon />
              <Typography variant="subtitle1">Supporting Documents</Typography>
            </Box>
            <Paper variant="outlined" style={{ padding: 16 }}>
              <FileListComponent
                files={isFilesUploaded ? filesUploaded : []}
                uploadId={uploadId}
                bucket={uploadBucket}
                onUpdate={fetchUploadedFiles}
                styleClasses={classes}
              />
              <Button
                color="primary"
                variant="outlined"
                onClick={toggleAddSupportingDocsModal}
                disabled={disable}
                startIcon={<AttachFileIcon />}
                className={classes.uploadButton}
              >
                {isFilesUploaded ? "Manage Documents" : "Upload Documents"}
              </Button>
            </Paper>
          </Grid>

          {/* Error messages */}
          <Snackbar
            open={isSnackbarOpen}
            autoHideDuration={4000}
            onClose={() => setIsSnackbarOpen(false)}
          >
            <Alert severity="error" onClose={() => setIsSnackbarOpen(false)}>
              {errorMessage}
            </Alert>
          </Snackbar>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActionContainer}>
        <Button
          variant="contained"
          color="secondary"
          onClick={onClose}
          type="reset"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddTicket}
          disabled={disable}
          type="submit"
          className={classes.submitButton}
        >
          Submit Ticket
        </Button>
      </DialogActions>

      {/* Supporting documents modal */}
      <ModalCard
        open={addSupportingDocsModal}
        onClose={toggleAddSupportingDocsModal}
      >
        <SBFileUploader
          bucketName={uploadBucket}
          folderName={uploadId}
          onClose={toggleAddSupportingDocsModal}
          withSubmit={handleIsFilesUploaded}
        />
      </ModalCard>
    </Dialog>
  );
}
